import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import sanitizer from '@helpers/sanitizer';
import { replaceNToBr } from '@helpers/utils/html-string';
import {
  ActionIdentifier,
  Timeline,
  TimelineShippingLabelAction,
  TimelineStep,
  TimelineStepStatus,
} from '@interfaces/models/timeline';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import { URL_QUERIES } from '@constants/orders-sales';
import { StepInfoProvider } from '@context/step-info.context';
import styles from '../product-timeline.module.scss';
import AddressModal from '../popin/get-shipping-label/address/shipping-address-modal';
import LabelView from './step-info/label';
import CancelationPolicyView from './step-info/cancelation-policy';
import TrackingView from './step-info/tracking';
import PaymentInfoView from './step-info/payment-info';
import ActionsView from './step-info/actions';
import ShippingInfo from './step-info/shipping-details/shipping-info';

function containerStyle(status: TimelineStepStatus, lastStep: boolean): string {
  return status === 'ongoing' ||
    status === 'pending' ||
    status === 'cancelled' ||
    (status === 'done' && lastStep === true)
    ? clsx(styles.ordersSales__mainRight__steps__step__box, styles['ordersSales__mainRight__steps__step__box--current'])
    : styles.ordersSales__mainRight__steps__step__box;
}

function titleStyle(status: TimelineStepStatus, lastStep: boolean): string {
  switch (status) {
    case 'done':
      if (lastStep) {
        return clsx(
          styles['ordersSales__mainRight__steps__step__box--text--bold'],
          styles['ordersSales__mainRight__steps__step__box--text--875'],
        );
      } else {
        return clsx(
          styles['ordersSales__mainRight__steps__step__box--text--bold'],
          styles['ordersSales__mainRight__steps__step__box--text--grey'],
          styles['ordersSales__mainRight__steps__step__box--text--875'],
        );
      }
    case 'cancelled':
    case 'ongoing':
    case 'pending':
      return clsx(
        styles['ordersSales__mainRight__steps__step__box--text--bold'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
    case 'upcoming':
      return clsx(
        styles['ordersSales__mainRight__steps__step__box--text--bold'],
        styles['ordersSales__mainRight__steps__step__box--text--lightGrey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
    case 'none':
      return '';
  }
}

function messageStyle(status: TimelineStepStatus): string {
  return status === 'upcoming'
    ? styles['ordersSales__mainRight__steps__step__box--text--none']
    : styles['ordersSales__mainRight__steps__step__box--text--875'];
}

function additionalStyle(status: TimelineStepStatus): string {
  return status === 'upcoming'
    ? styles['ordersSales__mainRight__steps__step__box--text--none']
    : clsx(
        styles['ordersSales__mainRight__steps__step__box--text--grey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
}

function dateStyle(status: TimelineStepStatus): string {
  return status === 'upcoming'
    ? clsx(
        styles['ordersSales__mainRight__steps__step__box--text--lightGrey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      )
    : clsx(
        styles['ordersSales__mainRight__steps__step__box--text--grey'],
        styles['ordersSales__mainRight__steps__step__box--text--875'],
      );
}

const ContainerView: React.FC<{
  order: Order | null;
  product: Product;
  step: TimelineStep;
  timeline: Timeline;
}> = ({ product, order, step, timeline }) => {
  const { kind, timelineType, analyticsTracking, status: timelineStatus } = timeline;

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const [showAddressForm, setShowAddressForm] = useState(false);

  const {
    status,
    title,
    message,
    additionalInfo,
    displayDate,
    estimatedDisplayDate,
    nextStepStatus,
    pickUpLocationDetails,
    proofOfDelivery,
    cancellationPolicy,
    identifier,
    note,
    messageKey,
    deliveryType,
    hyperlink,
    shipmentDetails,
    actions,
  } = step;

  const lastStep = nextStepStatus === 'none';
  const date = displayDate || estimatedDisplayDate;
  const shouldShowMessage = message && pickUpLocationDetails == null;
  const userKind = kind === 'Seller' ? 'seller' : 'buyer';
  const isCurrentCard =
    status === 'ongoing' || status === 'pending' || status === 'cancelled' || (status === 'done' && lastStep === true);

  const isQcCard = ['buyerItemAssessment', 'sellerItemAssessment'].includes(identifier);

  const isWaitingToBeShippedCard = timelineStatus === 'shipmentInProgress' && identifier === 'sellerShipment';
  const shippingAction = actions ? (actions[ActionIdentifier.shippingLabel] as TimelineShippingLabelAction) : undefined;

  const analyticData = { label: userKind, product_id: product.productId };

  const timelineMessage = hyperlink?.message || message;

  useEffect(() => {
    if (isCurrentCard) {
      // current tab
      const section = new URLSearchParams(window.location.search).get(URL_QUERIES.section);
      const source = section === '1' ? 'orders' : 'sales';
      const property = {
        tab_type: source, // Order or Sale
        card_name: identifier,
        status: status,
        message_key: messageKey || null,
        delivery_option: deliveryType || null,
      };

      sendAnalyticEvent('timeline_view', {
        label: userKind,
        property: JSON.stringify(property),
        product_id: product.productId,
      });
    }
  }, []);

  // TODO: EAA - Check keyboard flow for this action
  const onShipmentTracking = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (target && target.tagName === 'A') {
      const href = (target as HTMLAnchorElement).href;
      sendAnalyticEvent('timeline_hyperlink', {
        action: href,
        label: userKind,
        property: shipmentDetails ? 'next_steps_info' : 'shipping_instruction',
      });
    }
  };

  const podMessage = proofOfDelivery ? (
    <p className={additionalStyle(status)}>
      <span dangerouslySetInnerHTML={{ __html: sanitizer(replaceNToBr(`${proofOfDelivery.message} `, true)) }} />
      <span
        className={styles.ordersSales__mainRight__link}
        role="presentation"
        onClick={() => {
          sendAnalyticEvent('timeline_home_delivery_confirmation', { ...analyticData, action: 'proof_of_delivery' });
          window.open(proofOfDelivery.url, '_blank');
        }}
      >
        {proofOfDelivery.title}
      </span>
    </p>
  ) : undefined;

  return (
    <StepInfoProvider
      userType={userKind}
      isShowAddressForm={showAddressForm}
      showAddressForm={setShowAddressForm}
      shipmentDetails={shipmentDetails}
    >
      <div
        data-cy={`timeline-card-container-${identifier}`}
        className={containerStyle(status, lastStep)}
      >
        <LabelView status={status} />

        <div className={titleStyle(status, lastStep)}>{title}</div>

        {shouldShowMessage && (
          <div
            className={messageStyle(status)}
            dangerouslySetInnerHTML={{ __html: sanitizer(replaceNToBr(timelineMessage, true)) }}
          />
        )}

        {podMessage}

        {cancellationPolicy && <CancelationPolicyView cancellationPolicy={cancellationPolicy} />}

        {additionalInfo &&
          (isQcCard ? (
            <div
              dangerouslySetInnerHTML={{ __html: sanitizer(replaceNToBr(additionalInfo)) }}
              className={styles.ordersSales__mainRight__steps__step__box__paymentInfo__warningMessage}
            />
          ) : (
            <div
              className={additionalStyle(status)}
              dangerouslySetInnerHTML={{ __html: sanitizer(replaceNToBr(additionalInfo, true)) }}
            />
          ))}

        {note && (
          <div
            dangerouslySetInnerHTML={{ __html: sanitizer(replaceNToBr(note, true)) }}
            className={styles.ordersSales__mainRight__steps__step__box__paymentInfo__warningMessage}
            onClick={isWaitingToBeShippedCard ? onShipmentTracking : undefined}
          />
        )}

        <TrackingView
          productId={product.productId}
          userKind={userKind}
          directShipping={timelineType && timelineType === 'directShipping'}
          step={step}
        />

        {step.payoutDetails && <PaymentInfoView paymentDetail={step.payoutDetails} />}

        {isWaitingToBeShippedCard && (
          <ShippingInfo
            shipmentDetails={shipmentDetails}
            action={Array.isArray(shippingAction) ? undefined : shippingAction}
          />
        )}

        <ActionsView
          order={order}
          product={product}
          step={step}
          analyticsTracking={analyticsTracking}
        />

        {date && <div className={dateStyle(status)}>{date}</div>}
      </div>

      <AddressModal />
    </StepInfoProvider>
  );
};

export default ContainerView;
